import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';

const Notices: React.FC = () => {
  const { notices, updateNotice } = useAdmin();
  const [selectedNotice, setSelectedNotice] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const noticesPerPage = 10;

  const importantNotices = useMemo(() => notices.filter(n => n.isImportant), [notices]);
  const regularNotices = useMemo(() => notices.filter(n => !n.isImportant), [notices]);

  const totalPages = Math.ceil(regularNotices.length / noticesPerPage);
  const paginatedNotices = regularNotices.slice(
    (currentPage - 1) * noticesPerPage,
    currentPage * noticesPerPage
  );

  const handleNoticeClick = (notice: any) => {
    updateNotice(notice.id, { views: notice.views + 1 });
    setSelectedNotice({ ...notice, views: notice.views + 1 });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  // Notice detail view — article style
  if (selectedNotice) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="section-container py-8 sm:py-12">
          {/* Back arrow link */}
          <button
            onClick={() => setSelectedNotice(null)}
            className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-neutral-700 transition-colors mb-8 group cursor-pointer"
          >
            <svg className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Announcements
          </button>

          <article>
            {/* Badges */}
            <div className="flex items-center gap-2 mb-4">
              {selectedNotice.isImportant && (
                <span className="inline-flex items-center gap-1 bg-forest-50 text-forest-400 text-xs px-2.5 py-1 rounded-full font-semibold">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                  </svg>
                  Pinned
                </span>
              )}
              <span className="text-xs text-neutral-400 bg-neutral-100 px-2.5 py-1 rounded-full">Notice</span>
            </div>

            {/* Title */}
            <h1 className="font-display text-heading text-neutral-900 leading-tight mb-6">
              {selectedNotice.title}
            </h1>

            {/* Author + Date metadata bar */}
            <div className="flex items-center gap-4 pb-6 mb-8 border-b border-neutral-150">
              {/* Avatar placeholder */}
              <div className="w-9 h-9 bg-forest-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-forest-400">
                  {selectedNotice.author?.charAt(0)?.toUpperCase() || 'A'}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-900">{selectedNotice.author}</p>
                <p className="text-xs text-neutral-400">{formatDate(selectedNotice.createdAt)}</p>
              </div>
            </div>

            {/* Content */}
            <div className="prose prose-gray max-w-none text-neutral-700 leading-relaxed whitespace-pre-wrap text-base">
              {selectedNotice.content}
            </div>
          </article>
        </div>
      </div>
    );
  }

  // Notices list
  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Page Header */}
      <div className="bg-white border-b border-neutral-150">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h1 className="font-display text-display-sm text-neutral-900 mb-2">Announcements</h1>
          <p className="text-neutral-500 text-sm mb-6">Stay updated with the latest news and important updates from LiberaCure.</p>

          {/* Search/Filter bar (visual only) */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search announcements..."
              className="w-full pl-11 pr-4 py-3 bg-neutral-50 border border-neutral-150 rounded-xl text-sm text-neutral-700 placeholder-neutral-400 focus:ring-2 focus:ring-forest-500/20 focus:border-forest-500 focus:bg-white outline-none transition-all"
              readOnly
            />
          </div>
        </div>
      </div>

      <div className="section-container py-6 sm:py-8">
        {/* Important Notices — Featured pinned cards */}
        {importantNotices.length > 0 && (
          <div className="mb-6 space-y-3">
            {importantNotices.map((notice) => (
              <div
                key={notice.id}
                onClick={() => handleNoticeClick(notice)}
                className="group bg-white rounded-2xl border border-neutral-150 shadow-soft overflow-hidden cursor-pointer hover:shadow-md hover:border-forest-100 transition-all duration-200"
              >
                <div className="flex">
                  {/* Blue left accent bar */}
                  <div className="w-1 bg-forest-400 flex-shrink-0" />
                  <div className="flex-1 p-5 sm:p-6">
                    <div className="flex items-start gap-4">
                      {/* Megaphone icon */}
                      <div className="flex-shrink-0 w-10 h-10 bg-forest-50 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-forest-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                        </svg>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="inline-flex items-center text-[11px] font-semibold text-forest-400 bg-forest-50 px-2 py-0.5 rounded-full">Pinned</span>
                          <span className="text-[11px] text-neutral-400 ml-auto hidden sm:inline">{formatDate(notice.createdAt)}</span>
                        </div>
                        <h3 className="text-base font-semibold text-neutral-900 group-hover:text-forest-700 transition-colors mb-1">
                          {notice.title}
                        </h3>
                        {/* Excerpt */}
                        <p className="text-sm text-neutral-500 line-clamp-2">{notice.content}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Regular Notices — Card list with hover lift */}
        {paginatedNotices.length > 0 ? (
          <div className="space-y-2">
            {paginatedNotices.map((notice, i) => (
              <div
                key={notice.id}
                onClick={() => handleNoticeClick(notice)}
                className={`group flex items-center gap-4 px-5 sm:px-6 py-4 sm:py-5 rounded-xl cursor-pointer hover:shadow-sm hover:-translate-y-px transition-all duration-200 border border-transparent hover:border-neutral-150 ${
                  i % 2 === 0 ? 'bg-white' : 'bg-neutral-50/50'
                }`}
              >
                {/* Category tag */}
                <span className="hidden sm:inline-block text-[11px] font-medium text-neutral-400 bg-neutral-100 px-2 py-0.5 rounded flex-shrink-0">
                  Notice
                </span>

                {/* Title */}
                <h3 className="flex-1 text-sm sm:text-[15px] font-medium text-neutral-800 group-hover:text-neutral-900 transition-colors truncate">
                  {notice.title}
                </h3>

                {/* Date */}
                <span className="text-xs text-neutral-400 flex-shrink-0">{formatDate(notice.createdAt)}</span>

                {/* Arrow */}
                <svg className="w-4 h-4 text-neutral-300 group-hover:text-forest-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            ))}
          </div>
        ) : (
          /* Empty state */
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <svg className="w-7 h-7 text-neutral-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-neutral-700 mb-1">No announcements yet</h3>
            <p className="text-sm text-neutral-400">Check back later for updates and news from LiberaCure.</p>
          </div>
        )}

        {/* Pagination — Compact pill style */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-10">
            <div className="inline-flex items-center bg-white rounded-full border border-neutral-150 shadow-soft p-1 gap-0.5">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm text-neutral-500 hover:text-neutral-700 rounded-full transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-8 h-8 text-sm font-medium rounded-full transition-all duration-200 ${
                    currentPage === page
                      ? 'bg-forest-400 text-white'
                      : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm text-neutral-500 hover:text-neutral-700 rounded-full transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notices;
